import React, { useState, useEffect } from "react";
import { format, parse } from "date-fns";
import STRINGS from "../../Constants/Strings";
import { HTTP_STATUS } from "../../Communication/Constants";
import BranchModal from "../BranchModal";
import MemberHeader from "./MemberHeader";
import PersonalDetailsSection from "./PersonalDetailsSection";
import ContactDetailsSection from "./ContactDetailsSection";
import AddressDetailsSection from "./AddressDetailsSection";
import {
  EmploymentDetailsSection,
  TaxationDetailsSection,
  ComplianceDetailsSection,
} from "./ProfessionalTabSections";
import {
  CreditDetailsSection,
  KycDetailsSection,
  SecondaryIdSection,
} from "./CreditKycTabSections";
import {
  EditPersonalModal,
  EditContactModal,
  EditAddressModal,
  EditEmploymentModal,
  EditTaxationModal,
  EditComplianceModal,
} from "./modals";
import { maskSensitiveData, getNpaFromPhoneNumber } from "./utils";

const MemberDetails = ({
  selectedMember,
  account,
  showPersonalDetails,
  setShowPersonalDetails,
  showContactDetails,
  setShowContactDetails,
  showAddressDetails,
  setShowAddressDetails,
  showCreditDetails,
  setShowCreditDetails,
  showKycDetails,
  setShowKycDetails,
  showSecondaryIdDetails,
  setShowSecondaryIdDetails,
  memberDetailsTab,
  setMemberDetailsTab,
  handleBackToMembers,
  formatPhoneNumber,
  applicant,
  doUpdateApplicant,
  refreshMemberDetails,
  provinceList,
  employmentAndIncomeReducer,
  occupationCodes,
  idTypes,
  finInfo,
  complianceReducer,
  doFileList,
  doUploadFile,
  history,
}) => {
  console.log("MemberDetails", selectedMember, applicant, account);

  // Modal state management
  const [showEditModal, setShowEditModal] = useState(false);
  const [showContactEditModal, setShowContactEditModal] = useState(false);
  const [showAddressEditModal, setShowAddressEditModal] = useState(false);
  const [showEmploymentEditModal, setShowEmploymentEditModal] = useState(false);
  const [showTaxationEditModal, setShowTaxationEditModal] = useState(false);
  const [showComplianceEditModal, setShowComplianceEditModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Professional tab state management
  const [showEmploymentDetails, setShowEmploymentDetails] = useState(true);
  const [showTaxationDetails, setShowTaxationDetails] = useState(true);
  const [showComplianceDetails, setShowComplianceDetails] = useState(true);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Add custom CSS for DatePicker
  useEffect(() => {
    try {
      if (
        document.head &&
        !document.head.querySelector("style[data-datepicker-fix]")
      ) {
        const datePickerStyle = document.createElement("style");
        datePickerStyle.setAttribute("data-datepicker-fix", "true");
        datePickerStyle.textContent = `
          .react-datepicker-popper {
            position: static !important;
            transform: none !important;
            z-index: 99999 !important;
            margin-top: 8px !important;
          }
          .react-datepicker {
            z-index: 99999 !important;
            font-size: 12px !important;
            border: 1px solid #dee2e6 !important;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1) !important;
          }
          .react-datepicker__portal {
            z-index: 99999 !important;
          }
          .react-datepicker__header {
            padding: 0 !important;
          }
          .react-datepicker__month-container {
            width: 280px !important;
          }
          .react-datepicker__day-names,
          .react-datepicker__week {
            display: flex !important;
            justify-content: space-between !important;
          }
          .react-datepicker__day-name,
          .react-datepicker__day {
            width: 32px !important;
            height: 32px !important;
            line-height: 32px !important;
            margin: 1px !important;
            font-size: 12px !important;
          }
          .custom-datepicker-header {
            display: flex !important;
            flex-direction: column !important;
            padding: 8px !important;
            margin: 0px 0 !important;
          }
          .custom-datepicker-clear-section {
            display: flex !important;
            justify-content: center !important;
            margin-bottom: 8px !important;
            width: 100% !important;
          }
          .custom-datepicker-nav-section {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 8px !important;
            width: 100% !important;
          }
        `;

        if (document.head) {
          document.head.appendChild(datePickerStyle);
        }
      }
    } catch (error) {
      console.warn("Failed to add datepicker styles:", error);
    }
  }, []);

  // Handle edit button click
  const handleEditClick = () => {
    setShowEditModal(true);
  };

  // Handle contact edit button click
  const handleContactEditClick = () => {
    setShowContactEditModal(true);
  };

  // Handle address edit button click
  const handleAddressEditClick = () => {
    setShowAddressEditModal(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setShowEditModal(false);
  };

  // Handle contact modal close
  const handleContactModalClose = () => {
    setShowContactEditModal(false);
  };

  // Handle address modal close
  const handleAddressModalClose = () => {
    setShowAddressEditModal(false);
  };

  // Handle employment edit button click
  const handleEmploymentEditClick = () => {
    setShowEmploymentEditModal(true);
  };

  // Handle employment modal close
  const handleEmploymentModalClose = () => {
    setShowEmploymentEditModal(false);
  };

  // Handle taxation edit button click
  const handleTaxationEditClick = () => {
    setShowTaxationEditModal(true);
  };

  // Handle taxation modal close
  const handleTaxationModalClose = () => {
    setShowTaxationEditModal(false);
  };

  // Handle compliance edit button click
  const handleComplianceEditClick = () => {
    setShowComplianceEditModal(true);
  };

  // Handle compliance modal close
  const handleComplianceModalClose = () => {
    setShowComplianceEditModal(false);
  };

  // Handle save button click
  const handleSaveClick = (formData) => {
    console.log("Saving personal details:", formData);
    doUpdateApplicant(
      {
        member: {
          name: formData.firstName || "",
          middleName: formData.middleName || "",
          surname: formData.lastName || "",
          dob: formData.dateOfBirth
            ? format(
                parse(formData.dateOfBirth, "MM-dd-yyyy", new Date()),
                "yyyy-MM-dd"
              )
            : "",
        },
      },
      selectedMember.applicantId,
      (res) => {
        if (res.status === HTTP_STATUS.OK) {
          setShowEditModal(false);
          setShowSuccessModal(true);
        }
      }
    );
  };

  // Handle contact save button click
  const handleContactSaveClick = (contactFormData) => {
    console.log("Saving contact details:", contactFormData);
    doUpdateApplicant(
      {
        address: {
          currentAddressCellPhoneArea: getNpaFromPhoneNumber(
            contactFormData.mobileNo
          ),
          currentAddressCellPhone: contactFormData.mobileNo
            ? contactFormData.mobileNo.slice(-7)
            : "",
          currentAddressPhoneArea: getNpaFromPhoneNumber(
            contactFormData.homeNo
          ),
          currentAddressPhone: contactFormData.homeNo
            ? contactFormData.homeNo.slice(-7)
            : "",
        },
        contact: {
          alternateContactArea: getNpaFromPhoneNumber(contactFormData.otherNo),
          alternateContactPhone: contactFormData.otherNo
            ? contactFormData.otherNo.slice(-7)
            : "",
        },
        member: {
          email: contactFormData.email || "",
        },
      },
      selectedMember.applicantId,
      (res) => {
        if (res.status === HTTP_STATUS.OK) {
          setShowContactEditModal(false);
          setShowSuccessModal(true);
        }
      }
    );
  };

  // Handle address save button click
  const handleAddressSaveClick = (addressFormData) => {
    console.log("Saving address details:", addressFormData);
    doUpdateApplicant(
      {
        address: {
          currentAddressLine1: addressFormData.currentAddressLine1 || "",
          currentAddressUnitNumber:
            addressFormData.currentAddressUnitNumber || "",
          currentAddressCity: addressFormData.currentAddressCity || "",
          currentAddressLine3: addressFormData.currentAddressCity || "",
          currentAddressProvince: addressFormData.currentAddressProvince || "",
          currentAddressPostalCode:
            addressFormData.currentAddressPostalCode || "",
          currentAddressCountry: addressFormData.currentAddressCountry || "CAN",
          mailingSameAsCivic: addressFormData.mailingSameAsCivic || false,
          mailingAddressLine1: addressFormData.mailingSameAsCivic
            ? ""
            : addressFormData.mailingAddressLine1 || "",
          mailingUnitNumber: addressFormData.mailingSameAsCivic
            ? ""
            : addressFormData.mailingUnitNumber || "",
          mailingCity: addressFormData.mailingSameAsCivic
            ? ""
            : addressFormData.mailingCity || "",
          mailingProvince: addressFormData.mailingSameAsCivic
            ? ""
            : addressFormData.mailingProvince || "",
          mailingPostalCode: addressFormData.mailingSameAsCivic
            ? ""
            : addressFormData.mailingPostalCode || "",
          mailingCountry: addressFormData.mailingSameAsCivic
            ? ""
            : addressFormData.mailingCountry || "CAN",
        },
      },
      selectedMember.applicantId,
      (res) => {
        if (res.status === HTTP_STATUS.OK) {
          setShowAddressEditModal(false);
          setShowSuccessModal(true);
        }
      }
    );
  };

  // Handle employment save button click
  const handleEmploymentSaveClick = (employmentFormData) => {
    console.log("Saving employment details:", employmentFormData);
    doUpdateApplicant(
      {
        employment: {
          employmentStatus: employmentFormData.employmentStatus || "",
          occupationCode: employmentFormData.occupationCode || "",
          jobTitle: employmentFormData.jobTitle || "",
          employerPosition: employmentFormData.employerPosition || "",
          currentEmployer: employmentFormData.currentEmployer || "",
          employerLine1: employmentFormData.employerLine1 || "",
          employerCity: employmentFormData.employerCity || "",
          employerProv: employmentFormData.employerProv || "",
          employerPc: employmentFormData.employerPc || "",
          employerPhoneArea: getNpaFromPhoneNumber(
            employmentFormData.employerPhone
          ),
          employerPhone: employmentFormData.employerPhone
            ? employmentFormData.employerPhone.slice(-7)
            : "",
          employerSalary: employmentFormData.employerSalary || "",
          incomeType: employmentFormData.incomeType || "",
        },
      },
      selectedMember.applicantId,
      (res) => {
        if (res.status === HTTP_STATUS.OK) {
          setShowEmploymentEditModal(false);
          setShowSuccessModal(true);
        }
      }
    );
  };

  // Handle taxation save button click
  const handleTaxationSaveClick = (taxationFormData) => {
    console.log("Saving taxation details:", taxationFormData);

    const {
      countryOption,
      countryName,
      areYouTaxResidentOfUsa,
      areYouTaxResidentOfOtherCountry,
      ssnUsa,
      ssn,
    } = taxationFormData;

    // Find selected option from country dropdown
    const selectedOption =
      countryOption && countryOption.length > 1
        ? countryOption.find((obj) => obj.taxResidentOtherName === countryName)
        : countryOption?.[0];

    let taxResidency = "";
    if (selectedOption?.taxResidencyResult) {
      taxResidency = selectedOption.taxResidencyResult.taxResidency;
    }

    // Build FATCA info based on country selection
    const fatcaInfo = {};
    if (countryName) {
      fatcaInfo.fatcaStatus =
        countryName === "USA" ||
        countryName === "US" ||
        countryName === "American" ||
        countryName === "United States" ||
        areYouTaxResidentOfUsa
          ? finInfo.fatca
          : finInfo.noFatcaCrs;

      if (finInfo.useCRSTypeFieldFlag) {
        if (
          countryName === "USA" ||
          countryName === "US" ||
          countryName === "American" ||
          areYouTaxResidentOfUsa
        ) {
          fatcaInfo.fatcaType = finInfo.noFatcaCrs;
        } else {
          fatcaInfo.fatcaType = finInfo.oecdCrs;
        }
      } else {
        fatcaInfo.fatcaStatus = finInfo.oecdCrs;
      }
    } else {
      fatcaInfo.fatcaStatus = finInfo.noFatcaCrs;
      if (finInfo.useCRSTypeFieldFlag) {
        fatcaInfo.fatcaType = finInfo.noFatcaCrs;
      }
    }

    // Handle regulatory fields model
    const updateRegulatoryFieldsInputModel = [];
    if (finInfo.useRegulatoryFieldsModel) {
      delete fatcaInfo.fatcaType;
      fatcaInfo.fatcaStatus = finInfo.noFatcaCrs;

      if (ssnUsa) {
        fatcaInfo.fatcaStatus = finInfo.fatca;
        taxResidency =
          finInfo.config?.taxResidency?.find((item) => item.taxResidentUs)
            ?.options[0]?.taxResidencyResult?.taxResidency || "";
      }

      if (areYouTaxResidentOfOtherCountry) {
        if (!ssnUsa) {
          taxResidency = "";
        }
        updateRegulatoryFieldsInputModel.push(
          {
            Code: 1,
            Value: finInfo.oecdCrs,
            Text: "OECD CRS Status",
            Type: "dropdown",
          },
          {
            Code: 2,
            Value: ssn || "",
            Text: "OECD CRS Tax ID",
            Type: "text",
          },
          {
            Code: 3,
            Value: selectedOption?.taxResidentOtherName || "",
            Text: "OECD Tax Residency",
            Type: "dropdown",
          }
        );
      } else {
        updateRegulatoryFieldsInputModel.push(
          {
            Code: 1,
            Value: finInfo.noFatcaCrs,
            Text: "OECD CRS Status",
            Type: "dropdown",
          },
          {
            Code: 2,
            Value: "",
            Text: "OECD CRS Tax ID",
            Type: "text",
          },
          {
            Code: 3,
            Value: "",
            Text: "OECD Tax Residency",
            Type: "dropdown",
          }
        );
      }
    }

    // Build the update object matching TaxReporting logic
    const updateData = {
      member: {
        sin: taxationFormData.sin || "",
      },
      regulatory: {
        ...fatcaInfo,
        taxResidency,
        ssn: ssn || "",
        taxResidencyOther: finInfo.useRegulatoryFieldsModel
          ? ""
          : countryName || "",
        updateRegulatoryFieldsInputModel,
      },
    };

    // For regulatory model, use ssnUsa instead of ssn
    if (finInfo.useRegulatoryFieldsModel) {
      updateData.regulatory.ssn = ssnUsa || "";
    }

    doUpdateApplicant(updateData, selectedMember.applicantId, (res) => {
      if (res.status === HTTP_STATUS.OK) {
        setShowTaxationEditModal(false);
        setShowSuccessModal(true);
      }
    });
  };

  // Handle compliance save button click
  const handleComplianceSaveClick = (complianceFormData) => {
    console.log("Saving compliance details:", complianceFormData);

    const updateData = {
      member: {
        intendedUseOfMembership:
          complianceFormData.intendedUseOfMembership || "",
      },
      regulatory: {
        memberHasThirdParty: complianceFormData.isThirdPartyAccount || false,
        memberIsPedp: complianceFormData.memberPEPDomestic || false,
        memberIsPefp: complianceFormData.memberPEPForeign || false,
        memberIsHio: complianceFormData.memberHIO || false,
        memberOfficeHeld: complianceFormData.areYouPEPorHIO
          ? complianceFormData.memberOfficeHeld || ""
          : "",
        memberJurisdiction: complianceFormData.areYouPEPorHIO
          ? complianceFormData.memberJurisdiction || ""
          : "",
        memberFundsSource: complianceFormData.areYouPEPorHIO
          ? complianceFormData.memberFundsSource || ""
          : "",
      },
    };

    // Add third party data if applicable
    if (complianceFormData.isThirdPartyAccount) {
      updateData.thirdParties = [
        {
          name: complianceFormData.thirdPartyName || "",
          relationship: complianceFormData.thirdPartyRelationship || "",
        },
      ];
    } else {
      updateData.thirdParties = [];
    }

    // Add PEP data if applicable
    if (complianceFormData.areYouPEPorHIO) {
      let jurisdictionType = "";
      if (complianceFormData.memberPEPDomestic) {
        jurisdictionType = "DOMESTIC";
      } else if (complianceFormData.memberPEPForeign) {
        jurisdictionType = "FOREIGN";
      }

      updateData.peps = [
        {
          name: applicant?.member?.name || "",
          relationship: "Self",
          jurisdictionType: jurisdictionType,
          hio: complianceFormData.memberHIO || false,
          officeHeld: complianceFormData.memberOfficeHeld || "",
          jurisdiction: complianceFormData.memberJurisdiction || "",
          fundsSource: complianceFormData.memberFundsSource || "",
        },
      ];
    } else {
      updateData.peps = [];
    }

    doUpdateApplicant(updateData, selectedMember.applicantId, (res) => {
      if (res.status === HTTP_STATUS.OK) {
        setShowComplianceEditModal(false);
        setShowSuccessModal(true);
      }
    });
  };

  // Handle secondary ID save button click
  const handleSecondaryIdSaveClick = (
    secondaryIdFormData,
    onSuccessCallback
  ) => {
    console.log("Saving secondary ID details:", secondaryIdFormData);
    doUpdateApplicant(
      {
        member: {
          idType1: secondaryIdFormData.idType1 || "",
          placeOfIssuance1: secondaryIdFormData.placeOfIssuance1 || "",
          issueDate1: secondaryIdFormData.issueDate1
            ? format(
                parse(secondaryIdFormData.issueDate1, "MM-dd-yyyy", new Date()),
                "yyyy-MM-dd"
              )
            : "",
          expiryDate1: secondaryIdFormData.expiryDate1
            ? format(
                parse(
                  secondaryIdFormData.expiryDate1,
                  "MM-dd-yyyy",
                  new Date()
                ),
                "yyyy-MM-dd"
              )
            : "",
          idDetail1: secondaryIdFormData.idDetail1 || "",
        },
      },
      selectedMember.applicantId,
      (res) => {
        if (res.status === HTTP_STATUS.OK) {
          if (onSuccessCallback) {
            onSuccessCallback();
          }
          setShowSuccessModal(true);
        }
      }
    );
  };

  return (
    <div
      className="member-details-view"
      style={{ padding: "16px 0 16px 0", height: "100%", position: "relative" }}
    >
      <MemberHeader
        selectedMember={selectedMember}
        handleBackToMembers={handleBackToMembers}
        applicant={applicant}
        account={account}
      />

      {/* Member Details Tabs */}
      <div className="[ branch-tab-container ]">
        <ul className="[ nav nav-tabs w-100 d-flex ]">
          <li className="[ nav-item flex-fill text-center ]" key={"personal"}>
            <button
              type="button"
              className={
                memberDetailsTab === "personal"
                  ? "[ form-subtitle nav-link active ]"
                  : "[ form-subtitle nav-link inactive ]"
              }
              data-toggle="tab"
              role="tab"
              aria-controls={"personal"}
              onClick={() => setMemberDetailsTab("personal")}
            >
              Personal
            </button>
            <div
              className={memberDetailsTab === "personal" ? "[ tab-line ]" : ""}
            ></div>
          </li>
          <li
            className="[ nav-item flex-fill text-center ]"
            key={"professional"}
          >
            <button
              type="button"
              className={
                memberDetailsTab === "professional"
                  ? "[ form-subtitle nav-link active ]"
                  : "[ form-subtitle nav-link inactive ]"
              }
              data-toggle="tab"
              role="tab"
              aria-controls={"professional"}
              onClick={() => setMemberDetailsTab("professional")}
            >
              Professional
            </button>
            <div
              className={
                memberDetailsTab === "professional" ? "[ tab-line ]" : ""
              }
            ></div>
          </li>
          <li className="[ nav-item flex-fill text-center ]" key={"credit-kyc"}>
            <button
              type="button"
              className={
                memberDetailsTab === "credit-kyc"
                  ? "[ form-subtitle nav-link active ]"
                  : "[ form-subtitle nav-link inactive ]"
              }
              data-toggle="tab"
              role="tab"
              aria-controls={"credit-kyc"}
              onClick={() => setMemberDetailsTab("credit-kyc")}
            >
              Credit&nbsp;&amp;&nbsp;KYC
            </button>
            <div
              className={
                memberDetailsTab === "credit-kyc" ? "[ tab-line ]" : ""
              }
            ></div>
          </li>
        </ul>
      </div>

      {/* Tab Content */}
      {memberDetailsTab === "personal" && (
        <div className="mb-4">
          <PersonalDetailsSection
            selectedMember={selectedMember}
            applicant={applicant}
            showPersonalDetails={showPersonalDetails}
            setShowPersonalDetails={setShowPersonalDetails}
            handleEditClick={handleEditClick}
          />

          <ContactDetailsSection
            applicant={applicant}
            showContactDetails={showContactDetails}
            setShowContactDetails={setShowContactDetails}
            handleContactEditClick={handleContactEditClick}
            formatPhoneNumber={formatPhoneNumber}
            account={account}
          />

          <AddressDetailsSection
            applicant={applicant}
            showAddressDetails={showAddressDetails}
            setShowAddressDetails={setShowAddressDetails}
            handleAddressEditClick={handleAddressEditClick}
          />
        </div>
      )}

      {memberDetailsTab === "professional" && (
        <div className="mb-4">
          <EmploymentDetailsSection
            applicant={applicant}
            showEmploymentDetails={showEmploymentDetails}
            setShowEmploymentDetails={setShowEmploymentDetails}
            handleEmploymentEditClick={handleEmploymentEditClick}
            formatPhoneNumber={formatPhoneNumber}
            employmentAndIncomeReducer={employmentAndIncomeReducer}
            occupationCodes={occupationCodes}
          />

          <TaxationDetailsSection
            applicant={applicant}
            showTaxationDetails={showTaxationDetails}
            setShowTaxationDetails={setShowTaxationDetails}
            handleTaxationEditClick={handleTaxationEditClick}
            maskSensitiveData={maskSensitiveData}
          />

          <ComplianceDetailsSection
            applicant={applicant}
            showComplianceDetails={showComplianceDetails}
            setShowComplianceDetails={setShowComplianceDetails}
            handleComplianceEditClick={handleComplianceEditClick}
            complianceReducer={complianceReducer}
          />
        </div>
      )}

      {memberDetailsTab === "credit-kyc" && (
        <div className="mb-4">
          <CreditDetailsSection
            applicant={applicant}
            showCreditDetails={showCreditDetails}
            setShowCreditDetails={setShowCreditDetails}
          />

          <KycDetailsSection
            applicant={applicant}
            showKycDetails={showKycDetails}
            setShowKycDetails={setShowKycDetails}
            history={history}
          />

          <SecondaryIdSection
            applicant={applicant}
            showSecondaryIdDetails={showSecondaryIdDetails}
            setShowSecondaryIdDetails={setShowSecondaryIdDetails}
            provinceList={provinceList || []}
            idTypes={idTypes}
            finInfo={finInfo}
            handleSecondaryIdSaveClick={handleSecondaryIdSaveClick}
            selectedMember={selectedMember}
            doFileList={doFileList}
            doUploadFile={doUploadFile}
          />
        </div>
      )}

      {/* Edit Personal Details Modal */}
      <EditPersonalModal
        showEditModal={showEditModal}
        handleModalClose={handleModalClose}
        selectedMember={selectedMember}
        handleSaveClick={handleSaveClick}
        applicant={applicant}
      />

      {/* Edit Contact Details Modal */}
      <EditContactModal
        showContactEditModal={showContactEditModal}
        handleContactModalClose={handleContactModalClose}
        account={account}
        applicant={applicant}
        handleContactSaveClick={handleContactSaveClick}
        formatPhoneNumber={formatPhoneNumber}
      />

      {/* Edit Address Details Modal */}
      <EditAddressModal
        showAddressEditModal={showAddressEditModal}
        handleAddressModalClose={handleAddressModalClose}
        applicant={applicant}
        handleAddressSaveClick={handleAddressSaveClick}
        provinceList={provinceList || []}
      />

      {/* Edit Employment Details Modal */}
      <EditEmploymentModal
        showEmploymentEditModal={showEmploymentEditModal}
        handleEmploymentModalClose={handleEmploymentModalClose}
        applicant={applicant}
        handleEmploymentSaveClick={handleEmploymentSaveClick}
        formatPhoneNumber={formatPhoneNumber}
        employmentAndIncomeReducer={employmentAndIncomeReducer}
        occupationCodes={occupationCodes}
        provinceList={provinceList || []}
      />

      {/* Edit Taxation Details Modal */}
      <EditTaxationModal
        showTaxationEditModal={showTaxationEditModal}
        handleTaxationModalClose={handleTaxationModalClose}
        applicant={applicant}
        handleTaxationSaveClick={handleTaxationSaveClick}
        finInfo={finInfo}
      />

      {/* Edit Compliance Details Modal */}
      <EditComplianceModal
        showComplianceEditModal={showComplianceEditModal}
        handleComplianceModalClose={handleComplianceModalClose}
        applicant={applicant}
        handleComplianceSaveClick={handleComplianceSaveClick}
        indicateWhoArray={complianceReducer?.indicateWhoList || []}
        accountPurposeList={complianceReducer?.accountPurposeList || []}
      />

      {/* Success Modal */}
      <BranchModal
        show={showSuccessModal}
        onHide={setShowSuccessModal}
        title="Success!"
        description={
          STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
            .EDIT_SUCCESS_MODAL.DESCRIPTION
        }
        buttonText={STRINGS.BRANCH.BUTTONS.OK}
        onButtonClick={() => {
          setShowSuccessModal(false);
          refreshMemberDetails(selectedMember);
        }}
        isSuccess
      />
    </div>
  );
};

export default MemberDetails;
